import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import './Mermas.css';
import Header from '../common/Header';
import Footer from '../common/Footer';
import FormModal from '../common/FormModal';
import ConfirmModal from '../common/ConfirmModal';
import FormField from '../common/FormField';
import { useToast } from '../../contexts/ToastContext';
import { getApiUrl } from '../../utils/api';
import { FaPlus, FaTrash, FaBox, FaCalendarAlt, FaClipboardList, FaEye } from 'react-icons/fa';

const Mermas = () => {
  const [mermas, setMermas] = useState([]);
  const [mermasAgrupadas, setMermasAgrupadas] = useState([]);
  const [ingredientes, setIngredientes] = useState([]);
  const [opcionesIngredientes, setOpcionesIngredientes] = useState([]);
  const [ingredienteSeleccionado, setIngredienteSeleccionado] = useState(null);
  const [ingredientesAgregados, setIngredientesAgregados] = useState([]); // Lista de ingredientes para merma múltiple
  const [platillos, setPlatillos] = useState([]);
  const [opcionesPlatillos, setOpcionesPlatillos] = useState([]);
  const [platilloSeleccionado, setPlatilloSeleccionado] = useState(null);
  const [platillosAgregados, setPlatillosAgregados] = useState([]); // Lista de platillos con cantidad
  const [ingredientesReceta, setIngredientesReceta] = useState([]);
  const [ingredientesConsolidados, setIngredientesConsolidados] = useState([]); // Ingredientes totales
  const [modoSeleccion, setModoSeleccion] = useState('ingrediente'); // 'ingrediente' o 'platillo'
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [mermaAEliminar, setMermaAEliminar] = useState(null);
  const [mermaDetalle, setMermaDetalle] = useState(null);
  const [totalMermas, setTotalMermas] = useState(0);

  // Campos del formulario
  const [formData, setFormData] = useState({
    idIngrediente: '',
    cantidad: '',
    motivo: '',
    fecha: new Date().toISOString().split('T')[0]
  });

  // Filtros
  const [fechaInicio, setFechaInicio] = useState(new Date().toISOString().split('T')[0]);
  const [fechaFin, setFechaFin] = useState(new Date().toISOString().split('T')[0]);

  const toast = useToast();

  // Función para limitar a 3 decimales
  const limitarDecimales = (valor, decimales = 3) => {
    const num = parseFloat(valor);
    if (isNaN(num)) return 0;
    return Math.round(num * Math.pow(10, decimales)) / Math.pow(10, decimales);
  };

  // Limita decimales conservando el texto mientras se escribe (permite "0." , "0.001", etc.)
  const limitarDecimalesTexto = (valor, decimales = 3) => {
    let v = String(valor).replace(/[^\d.]/g, ''); // solo dígitos y punto
    const i = v.indexOf('.');
    if (i !== -1) {
      // conservar un solo punto y recortar la parte decimal
      v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, '').slice(0, decimales);
    }
    return v;
  };

  useEffect(() => {
    cargarIngredientes();
    cargarPlatillos();
    cargarMermas();
  }, []);

  useEffect(() => {
    cargarMermas();
  }, [fechaInicio, fechaFin]);

  const cargarIngredientes = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getIngredientes'
        })
      });

      const data = await response.json();

      if (data.success) {
        setIngredientes(data.data || []);

        // Convertir ingredientes al formato de react-select
        const opciones = (data.data || []).map(ingrediente => ({
          value: ingrediente.idIngrediente,
          label: ingrediente.nombre
        }));
        setOpcionesIngredientes(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar ingredientes:', error);
      toast.error('Error al cargar ingredientes');
    }
  };

  const cargarPlatillos = async () => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getTodosLosPlatillos'
        })
      });

      const data = await response.json();

      if (data.success) {
        setPlatillos(data.data || []);

        // Convertir platillos al formato de react-select
        const opciones = (data.data || []).map(platillo => ({
          value: platillo.idProducto,
          label: platillo.nombre
        }));
        setOpcionesPlatillos(opciones);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar platillos:', error);
      toast.error('Error al cargar platillos');
    }
  };

  const cargarIngredientesReceta = async (idProducto) => {
    try {
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getIngredientesProductoCompleto',
          idProducto: idProducto
        })
      });

      const data = await response.json();

      if (data.success) {
        // Agregar campo de cantidad a mermar (inicialmente igual a la cantidad de la receta)
        const ingredientesConCantidad = (data.data || []).map(ing => ({
          ...ing,
          cantidadMerma: ing.cantidad,
          seleccionado: true
        }));
        setIngredientesReceta(ingredientesConCantidad);
      } else {
        toast.error(data.message);
        setIngredientesReceta([]);
      }
    } catch (error) {
      console.error('Error al cargar ingredientes de receta:', error);
      toast.error('Error al cargar ingredientes de receta');
      setIngredientesReceta([]);
    }
  };

  const agregarIngrediente = () => {
    if (!ingredienteSeleccionado) {
      toast.error('Seleccione un ingrediente');
      return;
    }

    const cantidad = parseFloat(formData.cantidad);
    if (!formData.cantidad || isNaN(cantidad) || cantidad <= 0) {
      toast.error('La cantidad debe ser mayor a 0');
      return;
    }

    // Si el ingrediente ya está en la lista, sumar la cantidad
    const existente = ingredientesAgregados.find(i => i.idIngrediente === ingredienteSeleccionado.value);
    if (existente) {
      setIngredientesAgregados(prev => prev.map(i =>
        i.idIngrediente === ingredienteSeleccionado.value
          ? { ...i, cantidad: limitarDecimales(i.cantidad + cantidad, 3) }
          : i
      ));
    } else {
      setIngredientesAgregados(prev => [...prev, {
        idIngrediente: ingredienteSeleccionado.value,
        nombre: ingredienteSeleccionado.label,
        cantidad: limitarDecimales(cantidad, 3)
      }]);
    }

    // Limpiar selección para agregar el siguiente
    setIngredienteSeleccionado(null);
    setFormData(prev => ({ ...prev, cantidad: '' }));
  };

  const eliminarIngrediente = (idIngrediente) => {
    setIngredientesAgregados(prev => prev.filter(i => i.idIngrediente !== idIngrediente));
  };

  const cambiarCantidadIngrediente = (idIngrediente, nuevaCantidad) => {
    setIngredientesAgregados(prev => prev.map(i =>
      i.idIngrediente === idIngrediente ? { ...i, cantidad: nuevaCantidad } : i
    ));
  };

  const handlePlatilloChange = (selectedOption) => {
    setPlatilloSeleccionado(selectedOption);
  };

  const agregarPlatillo = async () => {
    if (!platilloSeleccionado) {
      toast.error('Seleccione un platillo');
      return;
    }

    // Verificar si ya está agregado
    if (platillosAgregados.find(p => p.idProducto === platilloSeleccionado.value)) {
      toast.error('Este platillo ya está en la lista');
      return;
    }

    try {
      // Obtener ingredientes del platillo (incluyendo preparados)
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'getIngredientesProductoCompleto',
          idProducto: platilloSeleccionado.value
        })
      });

      const data = await response.json();

      if (data.success) {
        const nuevoPlatillo = {
          idProducto: platilloSeleccionado.value,
          nombre: platilloSeleccionado.label,
          cantidad: 1,
          ingredientes: data.data || []
        };

        const nuevosAgregados = [...platillosAgregados, nuevoPlatillo];
        setPlatillosAgregados(nuevosAgregados);
        setPlatilloSeleccionado(null);
        consolidarIngredientes(nuevosAgregados);
      } else {
        toast.error('Error al obtener ingredientes del platillo');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al agregar platillo');
    }
  };

  const eliminarPlatillo = (idProducto) => {
    const nuevosAgregados = platillosAgregados.filter(p => p.idProducto !== idProducto);
    setPlatillosAgregados(nuevosAgregados);
    consolidarIngredientes(nuevosAgregados);
  };

  const cambiarCantidadPlatillo = (idProducto, nuevaCantidad) => {
    const cantidad = parseFloat(nuevaCantidad) || 0;
    const nuevosAgregados = platillosAgregados.map(p =>
      p.idProducto === idProducto ? { ...p, cantidad } : p
    );
    setPlatillosAgregados(nuevosAgregados);
    consolidarIngredientes(nuevosAgregados);
  };

  const consolidarIngredientes = (listaPlatillos) => {
    const consolidado = {};

    listaPlatillos.forEach(platillo => {
      platillo.ingredientes.forEach(ing => {
        const cantidadTotal = parseFloat(ing.cantidad) * platillo.cantidad;

        if (consolidado[ing.idIngrediente]) {
          consolidado[ing.idIngrediente].cantidadMerma += cantidadTotal;
          // Agregar origen si no está ya
          if (ing.origen === 'preparado' && ing.preparado) {
            if (!consolidado[ing.idIngrediente].preparados.includes(ing.preparado)) {
              consolidado[ing.idIngrediente].preparados.push(ing.preparado);
            }
          }
        } else {
          consolidado[ing.idIngrediente] = {
            idIngrediente: ing.idIngrediente,
            nombre: ing.nombre,
            cantidadMerma: cantidadTotal,
            seleccionado: true,
            origen: ing.origen || 'directo',
            preparados: ing.preparado ? [ing.preparado] : []
          };
        }
      });
    });

    // Limitar a 3 decimales
    const resultado = Object.values(consolidado).map(ing => ({
      ...ing,
      cantidadMerma: limitarDecimales(ing.cantidadMerma, 3)
    }));

    setIngredientesConsolidados(resultado);
  };

  const handleIngredienteConsolidadoChange = (idIngrediente, field, value) => {
    setIngredientesConsolidados(prev => prev.map(ing => {
      if (ing.idIngrediente === idIngrediente) {
        return { ...ing, [field]: value };
      }
      return ing;
    }));
  };

  const handleIngredienteRecetaChange = (index, field, value) => {
    setIngredientesReceta(prev => prev.map((ing, i) => {
      if (i === index) {
        return { ...ing, [field]: value };
      }
      return ing;
    }));
  };

  const cargarMermas = async () => {
    try {
      setLoading(true);

      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'getMermas',
          fechaInicio: fechaInicio,
          fechaFin: fechaFin
        })
      });

      const data = await response.json();

      if (data.success) {
        const mermasData = data.data || [];
        setMermas(mermasData);

        // Agrupar por idSalida
        const agrupadas = {};
        mermasData.forEach(m => {
          if (!agrupadas[m.idSalida]) {
            agrupadas[m.idSalida] = {
              idSalida: m.idSalida,
              fecha: m.fecha,
              motivo: m.motivo,
              ingredientes: []
            };
          }
          agrupadas[m.idSalida].ingredientes.push({
            idSalidaDetalle: m.idSalidaDetalle,
            idIngrediente: m.idIngrediente,
            nombreIngrediente: m.nombreIngrediente,
            cantidad: m.cantidad,
            costo: m.costo
          });
        });

        setMermasAgrupadas(Object.values(agrupadas));
        calcularTotal(mermasData);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al cargar mermas:', error);
      toast.error('Error al cargar mermas');
    } finally {
      setLoading(false);
    }
  };

  const calcularTotal = (mermasData) => {
    const total = mermasData.reduce((sum, merma) => sum + parseFloat(merma.cantidad || 0), 0);
    setTotalMermas(total);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const nuevaMerma = () => {
    setFormData({
      idIngrediente: '',
      cantidad: '',
      motivo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
    setIngredienteSeleccionado(null);
    setPlatilloSeleccionado(null);
    setPlatillosAgregados([]);
    setIngredientesAgregados([]);
    setIngredientesReceta([]);
    setIngredientesConsolidados([]);
    setModoSeleccion('ingrediente');
    setShowModal(true);
  };

  const guardarMerma = async () => {
    try {
      setGuardando(true);

      if (!formData.motivo.trim()) {
        toast.error('El motivo es obligatorio');
        return;
      }

      if (modoSeleccion === 'ingrediente') {
        // Modo ingrediente: uno o varios ingredientes en una sola salida
        // Construir lista final incluyendo la selección pendiente (si no se dio clic en "Agregar")
        const lista = ingredientesAgregados.map(i => ({ ...i }));
        const cantPendiente = parseFloat(formData.cantidad);
        if (ingredienteSeleccionado && !isNaN(cantPendiente) && cantPendiente > 0) {
          const existente = lista.find(i => i.idIngrediente === ingredienteSeleccionado.value);
          if (existente) {
            existente.cantidad = limitarDecimales(parseFloat(existente.cantidad) + cantPendiente, 3);
          } else {
            lista.push({
              idIngrediente: ingredienteSeleccionado.value,
              nombre: ingredienteSeleccionado.label,
              cantidad: limitarDecimales(cantPendiente, 3)
            });
          }
        }

        if (lista.length === 0) {
          toast.error('Debe agregar al menos un ingrediente');
          return;
        }

        const ingredientesPayload = lista
          .filter(i => parseFloat(i.cantidad) > 0)
          .map(i => ({ idIngrediente: i.idIngrediente, cantidad: i.cantidad }));

        if (ingredientesPayload.length === 0) {
          toast.error('Las cantidades deben ser mayores a 0');
          return;
        }

        const payload = {
          tipo: 'crearMermaMultiple',
          ingredientes: ingredientesPayload,
          motivo: formData.motivo,
          fecha: formData.fecha
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`${data.data.ingredientesRegistrados} ingredientes registrados como merma`);
          handleCloseModal();
          cargarMermas();
        } else {
          toast.error(data.message);
        }
      } else {
        // Modo platillo (múltiples ingredientes en una sola salida)
        if (platillosAgregados.length === 0) {
          toast.error('Debe agregar al menos un platillo');
          return;
        }

        const ingredientesSeleccionados = ingredientesConsolidados.filter(ing => ing.seleccionado && ing.cantidadMerma > 0);

        if (ingredientesSeleccionados.length === 0) {
          toast.error('Debe seleccionar al menos un ingrediente con cantidad mayor a 0');
          return;
        }

        // Preparar array de ingredientes para enviar
        const ingredientesPayload = ingredientesSeleccionados.map(ing => ({
          idIngrediente: ing.idIngrediente,
          cantidad: ing.cantidadMerma
        }));

        // Generar descripción de platillos
        const descripcionPlatillos = platillosAgregados
          .map(p => `${p.nombre} x${p.cantidad}`)
          .join(', ');

        const payload = {
          tipo: 'crearMermaMultiple',
          ingredientes: ingredientesPayload,
          motivo: `${formData.motivo} (${descripcionPlatillos})`,
          fecha: formData.fecha
        };

        const response = await fetch(getApiUrl(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload)
        });

        const data = await response.json();

        if (data.success) {
          toast.success(`${data.data.ingredientesRegistrados} ingredientes registrados como merma`);
          handleCloseModal();
          cargarMermas();
        } else {
          toast.error(data.message);
        }
      }
    } catch (error) {
      console.error('Error al guardar merma:', error);
      toast.error('Error al guardar merma');
    } finally {
      setGuardando(false);
    }
  };

  const confirmarEliminarMerma = (merma) => {
    setMermaAEliminar(merma);
    setShowDeleteModal(true);
  };

  const eliminarMerma = async () => {
    if (!mermaAEliminar) return;

    try {
      setGuardando(true);
      const response = await fetch(getApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tipo: 'eliminarMerma',
          idSalida: mermaAEliminar.idSalida
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Merma eliminada correctamente');
        setShowDeleteModal(false);
        setMermaAEliminar(null);
        cargarMermas();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Error al eliminar merma:', error);
      toast.error('Error al eliminar merma');
    } finally {
      setGuardando(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIngredienteSeleccionado(null);
    setPlatilloSeleccionado(null);
    setPlatillosAgregados([]);
    setIngredientesAgregados([]);
    setIngredientesReceta([]);
    setIngredientesConsolidados([]);
    setModoSeleccion('ingrediente');
    setFormData({
      idIngrediente: '',
      cantidad: '',
      motivo: '',
      fecha: new Date().toISOString().split('T')[0]
    });
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setMermaAEliminar(null);
  };

  const verDetalleMerma = (merma) => {
    setMermaDetalle(merma);
    setShowDetalleModal(true);
  };

  const handleCloseDetalleModal = () => {
    setShowDetalleModal(false);
    setMermaDetalle(null);
  };

  if (loading && mermas.length === 0) {
    return (
      <div className="mermas-container">
        <div className="loading">Cargando mermas...</div>
      </div>
    );
  }

  return (
    <div className="mermas-container">
      <Header
        titulo='Mermas'
        color='#795548'
      />
      <div className="mermas-content">
        <div className="mermas-inner">
          <div className="mermas-header">
            <h1 className="mermas-titulo">Registro de Mermas</h1>
            <div className="botones-header">
              <button
                onClick={nuevaMerma}
                className="btn-nuevo"
              >
                <FaPlus /> Nueva Merma
              </button>
            </div>
          </div>

          <div className="panel-filtros">
            <div className="filtros-content">
              <div className="filtros-row-horizontal">
                <div className="filtro-item">
                  <label>Fecha Inicio:</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="filtro-item">
                  <label>Fecha Fin:</label>
                  <input
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                    className="input-filtro"
                  />
                </div>

                <div className="btn-mostrar-mermas">
                  <button
                    className="btn-buscar"
                    onClick={cargarMermas}
                  >
                    Mostrar Mermas
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="panel-listado">
            <div className="tabla-container">
              {loading ? (
                <div className="cargando">Cargando mermas...</div>
              ) : mermasAgrupadas.length === 0 ? (
                <div className="sin-datos">No se encontraron mermas en el periodo seleccionado.</div>
              ) : (
                <table className="tabla-mermas">
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Motivo</th>
                      <th>Ingredientes</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mermasAgrupadas.map(merma => (
                      <tr key={merma.idSalida}>
                        <td>{merma.fecha}</td>
                        <td className="motivo-merma">{merma.motivo}</td>
                        <td className="cantidad-ingredientes">{merma.ingredientes.length}</td>
                        <td>
                          <div className="acciones-merma">
                            <button
                              onClick={() => verDetalleMerma(merma)}
                              className="btn-accion btn-ver"
                              title="Ver detalle"
                            >
                              <FaEye />
                            </button>
                            <button
                              onClick={() => confirmarEliminarMerma(merma)}
                              className="btn-accion btn-eliminar"
                              title="Eliminar merma"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="acciones-container">
              <div className="info-mermas">
                <span>Total de registros: {mermasAgrupadas.length}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {/* Modal para crear merma */}
      <FormModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title="Nueva Merma"
        onSave={guardarMerma}
        saveLabel="Registrar"
        isSaving={guardando}
        size="xlarge"
        className="modal-merma"
        saveIcon={<FaBox />}
        saveButtonType="button"
      >
        {/* Selector de modo */}
        <div className="modo-selector">
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'ingrediente' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('ingrediente');
              setPlatilloSeleccionado(null);
              setIngredientesReceta([]);
            }}
          >
            Por Ingrediente
          </button>
          <button
            type="button"
            className={`modo-btn ${modoSeleccion === 'platillo' ? 'activo' : ''}`}
            onClick={() => {
              setModoSeleccion('platillo');
              setIngredienteSeleccionado(null);
            }}
          >
            Por Platillo (Receta)
          </button>
        </div>

        {modoSeleccion === 'ingrediente' ? (
          <>
            <div className="agregar-platillo-row">
              <div className="select-platillo-container">
                <label className="campo-label-merma">Ingrediente</label>
                <Select
                  value={ingredienteSeleccionado}
                  onChange={(selectedOption) => setIngredienteSeleccionado(selectedOption)}
                  options={opcionesIngredientes}
                  placeholder="Buscar ingrediente..."
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => "No se encontraron ingredientes"}
                  className="select-producto"
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#795548' }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#795548' : state.isFocused ? '#f5f5f5' : 'white',
                      color: state.isSelected ? 'white' : '#333'
                    })
                  }}
                />
              </div>
              <div className="cantidad-ingrediente">
                <FormField
                  label="Cantidad"
                  type="text"
                  inputMode="decimal"
                  name="cantidad"
                  value={formData.cantidad}
                  onChange={(e) => {
                    const valor = limitarDecimalesTexto(e.target.value, 3);
                    setFormData(prev => ({ ...prev, cantidad: valor }));
                  }}
                  placeholder="0.000"
                />
              </div>
              <button
                type="button"
                className="btn-agregar-platillo"
                onClick={agregarIngrediente}
              >
                <FaPlus /> Agregar
              </button>
            </div>

            <FormField
              label="Fecha"
              type="date"
              name="fecha"
              value={formData.fecha}
              onChange={handleInputChange}
              icon={<FaCalendarAlt />}
            />

            {ingredientesAgregados.length > 0 ? (
              <div className="lista-platillos-agregados">
                <label>Ingredientes a mermar:</label>
                <div className="tabla-platillos">
                  <div className="header-platillos">
                    <span className="col-platillo-nombre">Ingrediente</span>
                    <span className="col-platillo-cantidad">Cantidad</span>
                    <span className="col-platillo-acciones">Acciones</span>
                  </div>
                  {ingredientesAgregados.map(ing => (
                    <div key={ing.idIngrediente} className="row-platillo">
                      <span className="col-platillo-nombre">{ing.nombre}</span>
                      <span className="col-platillo-cantidad">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={ing.cantidad}
                          onChange={(e) => cambiarCantidadIngrediente(ing.idIngrediente, limitarDecimalesTexto(e.target.value, 3))}
                        />
                      </span>
                      <span className="col-platillo-acciones">
                        <button
                          type="button"
                          className="btn-eliminar-platillo"
                          onClick={() => eliminarIngrediente(ing.idIngrediente)}
                        >
                          <FaTrash />
                        </button>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="sin-ingredientes">
                Selecciona un ingrediente y una cantidad, luego presiona "Agregar".
              </div>
            )}
          </>
        ) : (
          <>
            <div className="agregar-platillo-row">
              <div className="select-platillo-container">
                <Select
                  value={platilloSeleccionado}
                  onChange={handlePlatilloChange}
                  options={opcionesPlatillos}
                  placeholder="Buscar platillo..."
                  isSearchable={true}
                  isClearable={true}
                  noOptionsMessage={() => "No se encontraron platillos"}
                  className="select-producto"
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '42px',
                      borderColor: '#d1d5db',
                      '&:hover': { borderColor: '#795548' }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#795548' : state.isFocused ? '#f5f5f5' : 'white',
                      color: state.isSelected ? 'white' : '#333'
                    })
                  }}
                />
              </div>
              <button
                type="button"
                className="btn-agregar-platillo"
                onClick={agregarPlatillo}
              >
                <FaPlus /> Agregar
              </button>
              <div className="fecha-platillo">
                <FormField
                  label="Fecha"
                  type="date"
                  name="fecha"
                  value={formData.fecha}
                  onChange={handleInputChange}
                  icon={<FaCalendarAlt />}
                />
              </div>
            </div>

            <div className="platillo-layout">
              <div className="platillo-col">
                {platillosAgregados.length > 0 ? (
                  <div className="lista-platillos-agregados">
                    <label>Platillos a mermar:</label>
                    <div className="tabla-platillos">
                      <div className="header-platillos">
                        <span className="col-platillo-nombre">Platillo</span>
                        <span className="col-platillo-cantidad">Cantidad</span>
                        <span className="col-platillo-acciones">Acciones</span>
                      </div>
                      {platillosAgregados.map(platillo => (
                        <div key={platillo.idProducto} className="row-platillo">
                          <span className="col-platillo-nombre">{platillo.nombre}</span>
                          <span className="col-platillo-cantidad">
                            <input
                              type="number"
                              value={platillo.cantidad}
                              onChange={(e) => cambiarCantidadPlatillo(platillo.idProducto, limitarDecimales(e.target.value, 3))}
                              min="0.001"
                              step="0.001"
                            />
                          </span>
                          <span className="col-platillo-acciones">
                            <button
                              type="button"
                              className="btn-eliminar-platillo"
                              onClick={() => eliminarPlatillo(platillo.idProducto)}
                            >
                              <FaTrash />
                            </button>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="sin-ingredientes">
                    Agrega platillos para calcular su merma.
                  </div>
                )}
              </div>

              <div className="platillo-col">
                {ingredientesConsolidados.length > 0 && (
                  <div className="ingredientes-receta">
                    <label>Ingredientes consolidados a mermar:</label>
                    <div className="tabla-ingredientes-receta">
                      <div className="header-ingredientes">
                        <span className="col-check"></span>
                        <span className="col-nombre">Ingrediente</span>
                        <span className="col-origen">Origen</span>
                        <span className="col-merma">Cant. a Mermar</span>
                      </div>
                      {ingredientesConsolidados.map((ing) => (
                        <div key={ing.idIngrediente} className="row-ingrediente">
                          <span className="col-check">
                            <input
                              type="checkbox"
                              checked={ing.seleccionado}
                              onChange={(e) => handleIngredienteConsolidadoChange(ing.idIngrediente, 'seleccionado', e.target.checked)}
                            />
                          </span>
                          <span className="col-nombre">{ing.nombre}</span>
                          <span className="col-origen">
                            {ing.preparados && ing.preparados.length > 0
                              ? <span className="tag-preparado" title={ing.preparados.join(', ')}>Preparado</span>
                              : <span className="tag-directo">Directo</span>
                            }
                          </span>
                          <span className="col-merma">
                            <input
                              type="number"
                              value={limitarDecimales(ing.cantidadMerma, 3)}
                              onChange={(e) => handleIngredienteConsolidadoChange(ing.idIngrediente, 'cantidadMerma', limitarDecimales(e.target.value, 3))}
                              step="0.001"
                              min="0"
                              disabled={!ing.seleccionado}
                            />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {platillosAgregados.length > 0 && ingredientesConsolidados.length === 0 && (
                  <div className="sin-ingredientes">
                    Los platillos seleccionados no tienen ingredientes en sus recetas.
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        <FormField
          label="Motivo"
          type="textarea"
          name="motivo"
          value={formData.motivo}
          onChange={handleInputChange}
          required
          placeholder="Describa el motivo de la merma"
          icon={<FaClipboardList />}
        />
      </FormModal>

      {/* Modal de confirmacion para eliminar */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={eliminarMerma}
        title="Eliminar Merma"
        message={`¿Esta seguro de eliminar esta merma? Esta accion no se puede deshacer.`}
        confirmLabel="Eliminar"
        type="danger"
        isLoading={guardando}
      />

      {/* Modal para ver detalle de merma */}
      <FormModal
        isOpen={showDetalleModal}
        onClose={handleCloseDetalleModal}
        title="Detalle de Merma"
        size="large"
        showCancel={false}
        saveLabel="Cerrar"
        onSave={handleCloseDetalleModal}
        saveButtonType="button"
      >
        {mermaDetalle && (
          <div className="detalle-merma-modal">
            <div className="detalle-info">
              <p><strong>Fecha:</strong> {mermaDetalle.fecha}</p>
              <p><strong>Motivo:</strong> {mermaDetalle.motivo}</p>
            </div>
            <div className="detalle-ingredientes">
              <label>Ingredientes:</label>
              <table className="tabla-detalle-ingredientes">
                <thead>
                  <tr>
                    <th>Ingrediente</th>
                    <th>Cantidad</th>
                  </tr>
                </thead>
                <tbody>
                  {mermaDetalle.ingredientes.map(ing => (
                    <tr key={ing.idSalidaDetalle}>
                      <td>{ing.nombreIngrediente}</td>
                      <td className="cantidad-detalle">{parseFloat(ing.cantidad).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </FormModal>
    </div>
  );
};

export default Mermas;
